import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Question, MagnifyingGlass, X, CaretRight, ArrowRight, Play, DeviceMobile } from "@phosphor-icons/react";

/**
 * HelpCenter — floating "?" FAB + right-side sliding panel with a
 * searchable static FAQ list. Every FAQ card can expand inline OR
 * jump straight to the relevant in-app route. Mounted once inside
 * `Layout` so it appears on every authed page.
 *
 * No AI, no backend calls — pure static content. Matches the app's
 * dark theme via existing CSS tokens (bg-card, border-border, etc.).
 */

// Prewritten FAQs. Keep answers tight (1-2 sentences) and always point
// to an actionable route so the panel reduces friction, not adds it.
const FAQS = [
  {
    id: "add-account",
    q: "How do I add a new trading account?",
    a: "Head to Settings → Trading Accounts. Fill the name, broker, starting balance, and currency and click + Add Account. You can rename or update balances anytime via the pencil icon.",
    link: "/settings",
    linkLabel: "Open Settings",
    tags: ["account", "settings", "broker", "balance"],
  },
  {
    id: "upload-csv",
    q: "How do I upload a CSV trade statement?",
    a: "Go to Trades → Import CSV. Drop your broker's CSV or MT4/MT5 HTML statement — we auto-detect the columns for known brokers (Zerodha, Binance, Delta India, Exness, XM, Vantage, IC Markets, Pepperstone). Any new layout you approve is remembered as a template for next time.",
    link: "/trades",
    linkLabel: "Go to Trades",
    tags: ["csv", "upload", "import", "statement", "mt4", "mt5"],
  },
  {
    id: "start-prop",
    q: "How do I start a prop challenge?",
    a: "Open Prop Challenge, pick a firm template (FTMO, FundedNext, MFF, MFTC…) or design your own rulebook, choose account size and click Start Challenge. The rules engine enforces daily loss, drawdown, min days, and news lock live as you trade.",
    link: "/prop-challenge",
    linkLabel: "Start a challenge",
    tags: ["prop", "challenge", "ftmo", "fundednext", "simulator"],
  },
  {
    id: "connect-broker",
    q: "How do I connect a broker for auto-sync?",
    a: "Visit Settings → Manage connected brokers. Paste a read-only API key for Binance / CoinDCX / Delta India. Every night at 3 AM UTC we pull the last 24h of fills into your journal — no copy-paste.",
    link: "/settings/brokers",
    linkLabel: "Connect a broker",
    tags: ["broker", "binance", "coindcx", "delta", "auto sync", "api"],
  },
  {
    id: "view-chart",
    q: "How do I view a chart?",
    a: "Click Charts in the sidebar. Every symbol we support (forex, crypto, metals, oil, indices, MCX) has a full TradingView Advanced Charts widget with live ticks, indicators, and drawing tools.",
    link: "/charts",
    linkLabel: "Open charts",
    tags: ["chart", "tradingview", "candles", "forex", "btc"],
  },
  {
    id: "generate-cert",
    q: "How do I generate a prop-challenge certificate?",
    a: "Once you pass (or fail) a challenge, head to Certificates. Each card links to a scannable PDF certificate with your cert ID, trader name, and challenge stats — shareable via QR code on the verify page.",
    link: "/certificates",
    linkLabel: "View my certificates",
    tags: ["certificate", "pdf", "pass", "share", "download"],
  },
  {
    id: "analytics",
    q: "Where do I see my equity curve and stats?",
    a: "Analytics has the full breakdown: equity curve, win rate, R:R, expectancy, best/worst days, hourly heatmap, and per-symbol P&L.",
    link: "/analytics",
    linkLabel: "Open analytics",
    tags: ["equity", "stats", "win rate", "analytics", "curve"],
  },
  {
    id: "strategy",
    q: "How do I tag trades with a strategy?",
    a: "In Strategies you can define named playbooks (e.g. Break-and-Retest, ORB, Liquidity Sweep). Assign a strategy to each trade in the Trades table and Analytics breaks down performance by tag automatically.",
    link: "/strategies",
    linkLabel: "Manage strategies",
    tags: ["strategy", "tag", "playbook"],
  },
  {
    id: "billing",
    q: "How do I upgrade to Pro?",
    a: "Go to Billing and pick Monthly, Quarterly, or Yearly. Pro unlocks multi-account, unlimited trade history, broker auto-sync, MT4/MT5 statement import, and prop-challenge simulator without daily caps.",
    link: "/billing",
    linkLabel: "See plans",
    tags: ["pro", "upgrade", "billing", "subscription", "price"],
  },
  {
    id: "news-lock",
    q: "Why can't I place a trade right now?",
    a: "If a high-impact economic event (NFP, CPI, FOMC, Powell) is within ±2 minutes of your active challenge's affected currency, the news-trading lock blocks orders — just like a real prop firm. Wait for the release window to close or disable the rule in your challenge template.",
    link: "/prop-challenge",
    linkLabel: "Check active challenge",
    tags: ["news", "lock", "blocked", "nfp", "cpi", "fomc"],
  },
];

// Pinned "watch on mobile" video. Swap `VIDEO.url` here to change the
// tutorial — the modal auto-detects YouTube / Google-Drive / MP4 and
// picks the right embed strategy.
const VIDEO = {
  url: "https://drive.google.com/file/d/1J4s-_erGEoo5Pq4Zhi1nmwroWjYCk4oS/preview",
  title: "How to use TradeLog on mobile",
  duration: "2:14",
};

function VideoModal({ open, onClose, video }) {
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  // Detect the embed strategy from the URL shape.
  //  • YouTube      → iframe with autoplay query params
  //  • Google Drive → iframe with the `/preview` endpoint (already
  //                    supplies its own player + controls)
  //  • Anything else → assume it's a direct MP4/webm and use <video>
  const isIframe = /youtube\.com|youtu\.be|drive\.google\.com/.test(video.url);
  const iframeSrc = /youtube\.com|youtu\.be/.test(video.url)
    ? `${video.url}${video.url.includes("?") ? "&" : "?"}autoplay=1&rel=0`
    : video.url;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="help-video-modal"
    >
      <div
        className="relative w-full max-w-md sm:max-w-xl md:max-w-2xl aspect-[9/16] sm:aspect-video rounded-xl overflow-hidden bg-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition"
          aria-label="Close video"
          data-testid="help-video-modal-close"
        >
          <X size={18} weight="bold" />
        </button>
        {isIframe ? (
          <iframe
            src={iframeSrc}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video src={video.url} controls autoPlay className="w-full h-full" />
        )}
      </div>
    </div>
  );
}

export default function HelpCenter() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);

  // Close on Escape when open — standard modal affordance.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return FAQS;
    return FAQS.filter(f =>
      f.q.toLowerCase().includes(needle) ||
      f.a.toLowerCase().includes(needle) ||
      (f.tags || []).some(t => t.includes(needle))
    );
  }, [q]);

  return (
    <>
      {/* Floating '?' FAB — bottom-right, always visible over the app */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open help center"
        data-testid="help-center-fab"
        className="fixed bottom-32 xl:bottom-6 right-6 z-40 h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 hover:shadow-xl transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary/60"
      >
        <Question size={22} weight="bold" />
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          aria-hidden
        />
      )}

      {/* Slide-out panel — right side, full height on mobile, 420px on desktop */}
      <aside
        role="dialog"
        aria-label="Help Center"
        data-testid="help-center-panel"
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[420px] bg-card border-l border-border shadow-2xl transform transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"} flex flex-col`}
      >
        {/* Dark header with title + close */}
        <div className="bg-neutral-950 dark:bg-neutral-900 px-5 py-6 border-b border-border relative">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 h-8 w-8 rounded-full text-neutral-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition"
            aria-label="Close help center"
            data-testid="help-center-close"
          >
            <X size={18} weight="bold" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-8 w-8 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center">
              <Question size={16} weight="bold" className="text-primary" />
            </div>
            <h2 className="text-lg font-bold text-white">Help Center</h2>
          </div>
          <p className="text-sm text-neutral-400 mb-4">How can we help you today?</p>

          {/* Search bar */}
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute top-1/2 -translate-y-1/2 left-3 text-neutral-500 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search FAQs…"
              data-testid="help-center-search"
              className="w-full h-10 pl-9 pr-3 rounded-lg bg-neutral-900 dark:bg-neutral-950 border border-neutral-800 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:ring-1 focus:ring-primary/60 focus:border-primary/60"
              autoFocus
            />
          </div>
        </div>

        {/* FAQ list */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {/* Pinned mobile tutorial video — hidden when the user is
              actively searching so results stay focused. */}
          {!q.trim() && (
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              data-testid="help-video-card"
              className="w-full mb-4 group relative overflow-hidden rounded-xl border border-primary/30 hover:border-primary/60 bg-gradient-to-br from-primary/10 via-background/40 to-background text-left transition-all hover:shadow-lg hover:shadow-primary/10"
            >
              {/* Thumbnail area with faux-frame gradient and play button */}
              <div className="relative aspect-video bg-neutral-950 flex items-center justify-center overflow-hidden">
                {/* Ambient device silhouette so the card doesn't look empty
                    while a real thumbnail is not set yet. Swap for an
                    <img/> once the tutorial has a poster frame. */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(var(--primary)/0.25),transparent_60%)]" />
                <DeviceMobile size={80} weight="thin" className="absolute -left-4 bottom-2 text-primary/20" />
                <DeviceMobile size={56} weight="thin" className="absolute right-6 top-4 text-primary/10" />
                {/* Play button */}
                <div className="relative z-10 h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-xl transition-transform group-hover:scale-110">
                  <Play size={24} weight="fill" className="ml-0.5" />
                </div>
                {/* Duration pill */}
                <span className="absolute bottom-2 right-2 z-10 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-mono text-white">
                  {VIDEO.duration}
                </span>
              </div>
              {/* Text row */}
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-primary/80 font-semibold mb-0.5">Watch</p>
                  <p className="text-sm font-semibold leading-tight truncate">{VIDEO.title}</p>
                </div>
                <ArrowRight size={16} className="text-primary shrink-0 transition-transform group-hover:translate-x-0.5" weight="bold" />
              </div>
            </button>
          )}

          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3 px-1">
            {q.trim() ? `${results.length} result${results.length === 1 ? "" : "s"}` : "Popular FAQs"}
          </p>

          {results.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center">
              <p className="text-sm text-muted-foreground">
                No FAQ matches "<span className="text-foreground font-semibold">{q}</span>".
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Try broader keywords like "chart", "prop", or "csv".
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {results.map(f => {
                const isExpanded = expandedId === f.id;
                return (
                  <div
                    key={f.id}
                    data-testid={`help-faq-${f.id}`}
                    className="rounded-lg border border-border bg-background/40 hover:border-primary/40 transition-colors overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : f.id)}
                      className="w-full flex items-start gap-3 text-left px-4 py-3 hover:bg-secondary/40 transition"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-snug">{f.q}</p>
                      </div>
                      <CaretRight
                        size={14}
                        className={`shrink-0 mt-1 text-muted-foreground transition-transform ${isExpanded ? "rotate-90" : ""}`}
                      />
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-1 border-t border-border/60">
                        <p className="text-sm text-muted-foreground leading-relaxed mb-3">{f.a}</p>
                        {f.link && (
                          <Link
                            to={f.link}
                            onClick={() => setOpen(false)}
                            data-testid={`help-faq-link-${f.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                          >
                            {f.linkLabel || "Open"} <ArrowRight size={12} weight="bold" />
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — subtle contact hint */}
        <div className="border-t border-border px-5 py-3 bg-background/60 shrink-0">
          <p className="text-[11px] text-muted-foreground">
            Still stuck? Reach us at <a href="mailto:hello@thetradlog.com" className="text-primary hover:underline">hello@thetradlog.com</a>
          </p>
        </div>
      </aside>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} video={VIDEO} />
    </>
  );
}
